'use client';

import lockupLogo from '../../public/static/lockup-logo.svg';
import partnersImage from '../../public/static/partners.webp';

import { useIsMobileResolution } from '@/hooks/useIsMobileResolution';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import Link from 'next/link';

interface InfoFooterProps {
  className?: string;
}

export default function InfoFooter({ className }: InfoFooterProps) {
  const { isMobile, isTablet, isMounted } = useIsMobileResolution();

  // Use static year for SSR consistency
  const currentYear = 2026;

  return (
    <footer
      id='info-footer'
      data-testid='info-footer'
      role='contentinfo'
      tabIndex={-1}
      className={cn(
        'mx-[20px] mt-32 border-t border-border bg-background pb-[20px] pt-4 text-lg text-muted-foreground max-[1024px]:border-t-0 max-md:mt-20 max-md:flex max-md:flex-col max-md:gap-8 max-md:px-[20px] max-sm:mx-0',
        className
      )}>
      <div className='grid grid-cols-2 items-start justify-between max-[1024px]:grid-cols-1' suppressHydrationWarning>
        <div className='block'>
          <Image
            src={lockupLogo}
            alt='MASP - Museu de Arte de São Paulo Assis Chateaubriand'
            width={520}
            height={500}
            loading='lazy'
            sizes='(max-width: 1024px) 320px, 25vw'
            className='w-[25vw] max-[1024px]:w-80'
          />
        </div>

        {isMounted && !isMobile && !isTablet && (
          <div className='flex flex-col items-start justify-start max-[1024px]:hidden'>
            <div className='flex w-full items-center justify-between'>
              <div className='flex-1'>
                <ul className='text-foreground'>
                  <li className='w-fit text-lg font-medium hover:text-primary'>
                    <Link href='https://www.masp.org.br/sobre'>Sobre nós</Link>
                  </li>
                  <li className='w-fit text-lg font-medium hover:text-primary'>
                    <Link href='https://masp.org.br/acervo'>Artes e projetos</Link>
                  </li>
                  <li className='w-fit text-lg font-medium hover:text-primary'>
                    <Link href='https://www.masp.org.br/sobre'>Institucional</Link>
                  </li>
                  <li className='w-fit text-lg font-medium hover:text-primary'>
                    <Link href='https://www.masp.org.br/apoie'>Apoie</Link>
                  </li>
                  <li className='w-fit text-lg font-medium hover:text-primary'>
                    <Link href='https://www.masp.org.br/imprensa'>Imprensa</Link>
                  </li>
                </ul>
              </div>

              <div className='flex-1'>
                <ul className='text-foreground'>
                  <li className='w-fit text-lg font-medium hover:text-primary'>
                    <Link href='https://www.masp.org.br/sobre/fale-conosco'>Contato</Link>
                  </li>
                  <li className='w-fit text-lg font-medium hover:text-primary'>
                    <Link href='https://relatoconfidencial.com.br/masp/' className='w-fit hover:text-primary'>
                      Ouvidoria
                    </Link>
                  </li>
                  <li className='w-fit text-lg font-medium hover:text-primary'>
                    <Link href='/privacy-policy' className='w-fit hover:text-primary'>
                      Política de privacidade
                    </Link>
                  </li>
                  <li className='w-fit text-lg font-medium hover:text-primary'>
                    <Link href='/terms-of-use' className='w-fit hover:text-primary'>
                      Termos de uso
                    </Link>
                  </li>
                  <li className='w-fit text-lg font-medium hover:text-primary'>
                    <Link href='/cookies-policy' className='w-fit hover:text-primary'>
                      Política de cookies
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            <div className='my-18 flex flex-col items-start justify-center max-sm:mt-0'>
              <div className='flex items-center justify-start'>
                <Image
                  src={partnersImage}
                  alt='Patrocinadores e parceiros do MASP'
                  width={2000}
                  height={523}
                  loading='lazy'
                  quality={70}
                  sizes='40vw'
                  className='w-[40vw]'
                />
              </div>
            </div>

            <div className='flex items-center justify-center gap-8 text-sm'>
              <Link href='https://www.masp.org.br/' className='w-fit hover:text-primary'>
                &copy; 1947 - {currentYear} MASP
              </Link>
            </div>
          </div>
        )}

        {isMounted && (isMobile || isTablet) && (
          <div className='flex w-full flex-col items-start justify-start'>
            <ul className='mt-[20px] w-full text-foreground'>
              <li className='w-full border-t border-border py-5 font-medium hover:text-primary'>
                <Link href='https://www.masp.org.br/sobre'>Sobre nós</Link>
              </li>
              <li className='w-full border-t border-border py-5 font-medium hover:text-primary'>
                <Link href='https://masp.org.br/acervo'>Artes e projetos</Link>
              </li>
              <li className='w-full border-t border-border py-5 font-medium hover:text-primary'>
                <Link href='https://www.masp.org.br/sobre'>Institucional</Link>
              </li>
              <li className='w-full border-t border-border py-5 font-medium hover:text-primary'>
                <Link href='https://www.masp.org.br/apoie'>Apoie</Link>
              </li>
              <li className='w-full border-t border-border py-5 font-medium hover:text-primary'>
                <Link href='https://www.masp.org.br/imprensa'>Imprensa</Link>
              </li>
              <li className='w-full border-t border-border py-5 font-medium hover:text-primary'>
                <Link href='https://www.masp.org.br/sobre/fale-conosco'>Contato</Link>
              </li>
              <li className='w-full border-t border-border py-5 font-medium hover:text-primary'>
                <Link href='https://relatoconfidencial.com.br/masp/'>Ouvidoria</Link>
              </li>
              <li className='w-full border-t border-border py-5 font-medium hover:text-primary'>
                <Link href='/privacy-policy'>Política de privacidade</Link>
              </li>
              <li className='w-full border-t border-border py-5 font-medium hover:text-primary'>
                <Link href='/terms-of-use'>Termos de uso</Link>
              </li>
              <li className='w-full border-b border-t border-border py-5 font-medium hover:text-primary'>
                <Link href='/cookies-policy'>Política de cookies</Link>
              </li>
            </ul>

            <div className='my-24 flex flex-col items-start justify-center max-[1024px]:mb-8 max-[1024px]:mt-18'>
              <div className='flex items-center justify-start'>
                <Image
                  src={partnersImage}
                  alt='Patrocinadores e parceiros do MASP'
                  width={2000}
                  height={523}
                  loading='lazy'
                  quality={70}
                  sizes='100vw'
                  className='w-[100vw]'
                />
              </div>
            </div>

            <div className='flex items-center justify-center gap-8 text-sm'>
              <Link href='https://www.masp.org.br/' className='w-fit hover:text-primary'>
                &copy; 1947 - {currentYear} MASP
              </Link>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
}
